import React, { useEffect, useState } from 'react';
import { useDrawers } from '../../contexts/DrawerContext';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { ArrowLeft, CheckCircle2, ChevronRight } from 'lucide-react';
type Step = 1 | 2 | 3 | 4;
const MATTER_TYPES = ['Buying', 'Selling', 'Contract review', 'Property transfer', 'SMSF or trust', 'Commercial property', 'Subdivision', 'Solicitor certificate', 'Other'];
const inputClass = 'w-full h-12 px-4 rounded-xl bg-surface border border-border text-ink focus:border-accent focus:ring-2 focus:ring-accent/25 outline-none transition';
const labelClass = 'block text-sm font-medium text-ink mb-1.5';
export function QuoteDrawer() {
  const {
    isQuoteOpen,
    closeQuote
  } = useDrawers();
  const [step, setStep] = useState<Step>(1);
  const [matterType, setMatterType] = useState('');
  useEffect(() => {
    if (!isQuoteOpen) {
      setTimeout(() => {
        setStep(1);
        setMatterType('');
      }, 300);
    }
  }, [isQuoteOpen]);
  const handleNext = () => setStep(s => Math.min(s + 1, 4) as Step);
  const handleBack = () => setStep(s => Math.max(s - 1, 1) as Step);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep(4);
  };
  return <Drawer isOpen={isQuoteOpen} onClose={closeQuote} title="Get a free quote" subtitle={step < 4 ? `Step ${step} of 3` : undefined} data-id="element-40" data-mp="yibpsoq3t978">
      {step < 4 && <div className="px-6 pt-5 pb-3 bg-surface border-b border-border flex gap-2" data-id="element-41" data-mp="1q86ovd3rkd93">
          {[1, 2, 3].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${step >= i ? 'bg-accent' : 'bg-border'}`} data-id="element-42" data-mp="1v8c8q73skvgm" />)}
        </div>}

      <div className="p-6" data-id="element-43" data-mp="1lebxdnhx2tzg">
        {step === 1 && <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-44" data-mp="295hc1zc4fg86">
            <div data-id="element-45" data-mp="2dy0y7kovjjol">
              <h3 className="text-lg font-serif text-ink mb-1" data-id="element-46" data-mp="2dykb2mzo3hvr">
                What do you need help with?
              </h3>
              <p className="text-sm text-muted-ink" data-id="element-47" data-mp="2nqn3uyys8qoe">
                Select the primary matter type.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2.5" data-id="element-48" data-mp="2eri5bsn80xc8">
              {MATTER_TYPES.map(type => <button key={type} onClick={() => {
            setMatterType(type);
            handleNext();
          }} className={`flex items-center justify-between p-4 rounded-xl border text-left transition-all ${matterType === type ? 'border-accent bg-accent-soft text-accent-dark font-semibold' : 'border-border bg-surface hover:border-accent/40 hover:bg-accent-soft/40 text-ink'}`} data-id="element-49" data-mp="29tzu1vlqe612">
                  {type}
                  <ChevronRight className="w-4 h-4 opacity-50" data-id="element-50" data-mp="1m2krc1q1lmpa" />
                </button>)}
            </div>
          </div>}

        {step === 2 && <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-51" data-mp="171450wbsx0tu">
            <div className="flex items-center gap-3 mb-2" data-id="element-52" data-mp="1bzfgtclnplep">
              <button onClick={handleBack} className="p-2 -ml-2 rounded-full text-muted-ink hover:text-ink hover:bg-soft-panel transition-colors" aria-label="Back" data-id="element-53" data-mp="1c1s507i0cs07">
                <ArrowLeft className="w-5 h-5" data-id="element-54" data-mp="oacye2j77oj5" />
              </button>
              <div data-id="element-55" data-mp="o807gzvgq3ty">
                <h3 className="text-lg font-serif text-ink" data-id="element-56" data-mp="t97gf29cd927">
                  Property information
                </h3>
                <p className="text-sm text-muted-ink" data-id="element-57" data-mp="y7r1l0g42gti">
                  Tell us about the transaction.
                </p>
              </div>
            </div>

            <div className="space-y-4" data-id="element-58" data-mp="1313n31phu7i1">
              <div className="grid grid-cols-2 gap-4" data-id="element-59" data-mp="5kerhpr501h3">
                <div data-id="element-60" data-mp="5hisrz7gt8rl">
                  <label className={labelClass} data-id="element-61" data-mp="aiga69knhys1">Suburb</label>
                  <input type="text" className={inputClass} data-id="element-62" data-mp="agvgem65ujv8" />
                </div>
                <div data-id="element-63" data-mp="2c8ne2xwmfb6u">
                  <label className={labelClass} data-id="element-64" data-mp="2c5sxjhgqaaip">State</label>
                  <select className={`${inputClass} appearance-none`} data-id="element-65" data-mp="279u0l5hc3k6o">
                    <option data-id="element-66" data-mp="2lxf6t89i9zit">VIC</option>
                    <option data-id="element-67" data-mp="2lygp9xsupoak">NSW</option>
                    <option data-id="element-68" data-mp="2lzi1i5llsgtv">QLD</option>
                    <option data-id="element-69" data-mp="2h5xf7atdhbai">Other</option>
                  </select>
                </div>
              </div>

              <div data-id="element-70" data-mp="2ux271qqi0zaa">
                <label className={labelClass} data-id="element-71" data-mp="2uwk2m6tonera">Property type</label>
                <select className={`${inputClass} appearance-none`} data-id="element-72" data-mp="2qvqdxb8f1vxc">
                  <option data-id="element-73" data-mp="34waxwaq2hxsf">House</option>
                  <option data-id="element-74" data-mp="34v88k78e83em">Apartment or unit</option>
                  <option data-id="element-75" data-mp="2zxgfvazxcd9d">Townhouse</option>
                  <option data-id="element-76" data-mp="2zyrkfd171p1j">Vacant land</option>
                  <option data-id="element-77" data-mp="2zvnadz11rz2y">Commercial</option>
                </select>
              </div>

              <div data-id="element-78" data-mp="1akmh0309zl5e">
                <label className={labelClass} data-id="element-79" data-mp="15jfae79vcs0b">Sale method</label>
                <select className={`${inputClass} appearance-none`} data-id="element-80" data-mp="15l9rfai0rzz4">
                  <option data-id="element-81" data-mp="3h7ttpa0hcef0">Private sale</option>
                  <option data-id="element-82" data-mp="3h94xvaxu7o6n">Auction</option>
                  <option data-id="element-83" data-mp="3m653xluosqxr">Off-the-plan</option>
                  <option data-id="element-84" data-mp="3m7y9extkd546">Not applicable</option>
                </select>
              </div>

              <div data-id="element-85" data-mp="37gph2gya15nc">
                <label className={labelClass} data-id="element-86" data-mp="3cg2c3ozaxj10">Purchasing entity</label>
                <select className={`${inputClass} appearance-none`} data-id="element-87" data-mp="3ce9528ks0wsh">
                  <option data-id="element-88" data-mp="2yf9dot5vq3sm">Individual(s)</option>
                  <option data-id="element-89" data-mp="2yinj6a9uat4a">Company</option>
                  <option data-id="element-90" data-mp="33fnmwej7vjs8">Trust</option>
                  <option data-id="element-91" data-mp="33gp28v79msfr">SMSF</option>
                </select>
              </div>

              <div data-id="element-92" data-mp="2ojo7sjxowjdl">
                <label className={labelClass} data-id="element-93" data-mp="2ok7ro66whhu9">
                  Auction or signing deadline (if applicable)
                </label>
                <input type="date" className={inputClass} data-id="element-94" data-mp="mr02m51hjlv6" />
              </div>

              <Button fullWidth variant="accent" onClick={handleNext} className="mt-2" data-id="element-95" data-mp="mox9vd8y7emr">
                Continue to contact details
              </Button>
            </div>
          </div>}

        {step === 3 && <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-96" data-mp="gai2a9qicqc1">
            <div className="flex items-center gap-3 mb-2" data-id="element-97" data-mp="ca7tyqf8tnu0">
              <button type="button" onClick={handleBack} className="p-2 -ml-2 rounded-full text-muted-ink hover:text-ink hover:bg-soft-panel transition-colors" aria-label="Back" data-id="element-98" data-mp="18yvxkhq7p8jw">
                <ArrowLeft className="w-5 h-5" data-id="element-99" data-mp="z7j5bp0i4gdc" />
              </button>
              <div data-id="element-100" data-mp="v6zc2wj1twwh">
                <h3 className="text-lg font-serif text-ink" data-id="element-101" data-mp="v7ijhjb2p2w9">Contact details</h3>
                <p className="text-sm text-muted-ink" data-id="element-102" data-mp="1rqghkvca48lp">
                  Where should we send the quote?
                </p>
              </div>
            </div>

            <div className="space-y-4" data-id="element-103" data-mp="35ly9zhdxszof">
              <div data-id="element-104" data-mp="35mzuecgueyit">
                <label className={labelClass} data-id="element-105" data-mp="35nrht00adpy0">Full name</label>
                <input required type="text" autoComplete="name" className={inputClass} data-id="element-106" data-mp="3alsyjea8pd6b" />
              </div>
              <div data-id="element-107" data-mp="3jmisecfxwthe">
                <label className={labelClass} data-id="element-108" data-mp="3jn0ye0semm2i">Email</label>
                <input required type="email" autoComplete="email" className={inputClass} data-id="element-109" data-mp="3ol2fwha6mdbv" />
              </div>
              <div data-id="element-110" data-mp="24g2xc5znwp2">
                <label className={labelClass} data-id="element-111" data-mp="260yn541hloi">Phone</label>
                <input required type="tel" autoComplete="tel" className={inputClass} data-id="element-112" data-mp="66jqgz5xmrqp" />
              </div>
              <div data-id="element-113" data-mp="b3tgafx8h5f2">
                <label className={labelClass} data-id="element-114" data-mp="g47n5un3m9bi">Preferred contact method</label>
                <select className={`${inputClass} appearance-none`} data-id="element-115" data-mp="3n6zjkoj4jydr">
                  <option data-id="element-116" data-mp="3n54xh6w4a083">Email</option>
                  <option data-id="element-117" data-mp="3i852br0t2hvr">Phone call</option>
                  <option data-id="element-118" data-mp="3i73hwvxwgj1d">Either is fine</option>
                </select>
              </div>
              <div data-id="element-119" data-mp="yuclol8i97">
                <label className={labelClass} data-id="element-120" data-mp="1gys5lem2s7">Message (optional)</label>
                <textarea rows={3} className="w-full p-4 rounded-xl bg-surface border border-border text-ink focus:border-accent focus:ring-2 focus:ring-accent/25 outline-none transition resize-none" data-id="element-121" data-mp="3s742smbf377o"></textarea>
              </div>

              <Button fullWidth variant="accent" type="submit" className="mt-2" data-id="element-122" data-mp="3d91ww5gwy9n9">
                Submit request
              </Button>

              <p className="text-xs text-muted-ink text-center" data-id="element-123" data-mp="38gig6zmxiye8">
                Submitting information does not by itself create a
                solicitor-client relationship.
              </p>
            </div>
          </form>}

        {step === 4 && <div className="py-12 flex flex-col items-center text-center space-y-6 animate-in zoom-in-95 duration-300" data-id="element-124" data-mp="26mp632wipzs3">
            <div className="w-16 h-16 bg-accent-soft rounded-full flex items-center justify-center text-accent" data-id="element-125" data-mp="2beh0joisopni">
              <CheckCircle2 className="w-8 h-8" data-id="element-126" data-mp="1wqm7g70vkj9w" />
            </div>
            <div className="space-y-2" data-id="element-127" data-mp="21n2sgscs2hir">
              <h3 className="text-2xl font-serif text-ink" data-id="element-128" data-mp="21lrrf0apvnvc">Thank you.</h3>
              <p className="text-muted-ink leading-relaxed max-w-xs mx-auto" data-id="element-129" data-mp="2perskuvucaa9">
                The team will review the information and contact you about the
                proposed next step.
              </p>
            </div>
            <Button onClick={closeQuote} variant="outline" className="mt-2" data-id="element-130" data-mp="2fhdbqu609jhf">
              Close
            </Button>
          </div>}
      </div>
    </Drawer>;
}